import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // MSH: this store's product photos are only ever meant to differ by the
    // "Color" option - see blocks/msh-buybox-style.liquid's filename-based
    // photo filter, which keys exclusively off a "-Blk-"/"-WT-" marker in
    // each photo's filename and has no Style/Size logic anywhere in it.
    // (Per this fix's own report: live empirical re-confirmation of that
    // premise against festive-snowflakes-1 and a second product was blocked
    // this session - see the report for what to re-check before relying on
    // this comment alone.) Every option change dispatches the same
    // ProductSelectEvent though, and this handler used to unconditionally
    // `replaceWith()` the whole gallery node on every one of them, which is
    // a full node replacement, not a morph - the fresh node arrives from the
    // server in its default UNFILTERED state (all photos, catalog order)
    // and paints immediately, before msh-buybox-style.liquid's
    // MutationObserver notices the swap and re-applies the color filter a
    // beat later. That gap is the Style/Size flicker this change removes.
    // variant-picker.js's variantChanged() sets event.detail.changedOptionName
    // synchronously from the exact input that fired the underlying 'change'
    // event (not inferred/guessed), so this only ever skips the replace when
    // we are CERTAIN Color did not change on this specific click - every
    // other listener reacting to this same event (price, buy button, sticky
    // bar, etc.) is untouched by this check and keeps updating normally.
    const changedOptionName = /** @type {{ detail?: { changedOptionName?: string } }} */ (event).detail
      ?.changedOptionName;
    if (typeof changedOptionName === 'string' && changedOptionName && changedOptionName.toLowerCase() !== 'color') {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
